import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import deepEqual from 'fast-deep-equal';

import { CarrierPortalRequiredDocsCell } from './CarrierPortalRequiredDocsCell';

import { PageHeader } from 'components/PageHeader';
import { Pagination } from 'components/pagination/Pagination';
import { SearchType, SearchWithType } from 'components/search-with-type/SearchWithType';
import { ITableHeader, Table } from 'components/table/Table';
import { IPaginationVariables, usePagination } from '../../../hooks/usePagination';
import { formatDate } from '../../../utils/date';
import { decimateAndFormatNumber } from '../../../utils/number';
import { calculateLinesTotalExclVat } from '../../order/utils/price';
import { useTranslation } from '../../../contexts/translation-context';
import { PageHeading } from 'components/PageHeading';
import { Breadcrumb } from 'components/Breadcrumb';
import { ListPage } from 'components/page-templates/ListPage';
import { getCarrierPortalOrders, CarrierPortalOrder, CarrierPortalOrdersFilters } from '../models';
import { useCarrierPortalSupplier } from '../CarrierPortalCarrierContext';
import { RequiredDocument } from '../../order/utils/document-validation';

export const CarrierPortalOrdersPage = () => {
  const { i18n, lang } = useTranslation();
  const { carrierId } = useCarrierPortalSupplier();

  const searchTypes: SearchType[] = [
    {
      label: i18n('customerPortal.orders.invoiceRef'),
      value: 'customerRef',
    },
    {
      label: i18n('customerPortal.orders.orderRef'),
      value: 'orderNumber',
    },
  ];

  const tableHeaders: ITableHeader[] = [
    {
      id: 'id',
      name: i18n('carrierPortal.orders.orderRef'),
    },
    {
      id: 'loadLocation',
      name: i18n('carrierPortal.orders.loadLocation'),
    },
    {
      id: 'unloadLocation',
      name: i18n('carrierPortal.orders.unloadLocation'),
    },
    {
      id: 'loadDate',
      name: i18n('carrierPortal.orders.loadLocation'),
    },
    {
      id: 'unloadDate',
      name: i18n('carrierPortal.orders.unloadLocation'),
    },
    {
      id: 'saleAmount',
      name: i18n('carrierPortal.orders.invoiceTotal'),
    },
    {
      id: 'documents',
      name: i18n('carrierPortal.orders.requiredDocsTitle'),
    },
  ];

  // Filter values
  const [search, setSearch] = useState({
    value: '',
    type: searchTypes[0].value,
  });

  const [filters, setFilters] = useState<CarrierPortalOrdersFilters>({});
  const [refreshKey, setRefreshKey] = useState(0);
  const pageFetcher = useCallback(
    async (variables: IPaginationVariables) => {
      return getCarrierPortalOrders(carrierId, lang, filters, variables.cursor, variables.take);
    },
    [carrierId, lang, filters, refreshKey],
  );
  const page = usePagination<CarrierPortalOrder>({
    key: 'id',
    pageSize: 50,
    initialCursor: undefined,
    fetcher: pageFetcher,
  });

  useEffect(() => {
    page.reset();
  }, [filters]);

  useEffect(() => {
    const timeoutRef = setTimeout(() => {
      const newFilters: CarrierPortalOrdersFilters = {};

      const applySearch = (value: string, type: string) => {
        switch (type) {
          case 'orderNumber':
            newFilters.orderNumber = value;
            break;
          case 'carrierRef':
            newFilters.carrierRef = value;
            break;
        }
      };

      if (search.value) {
        applySearch(search.value, search.type);
      }

      setFilters((prev) => {
        if (deepEqual(prev, newFilters)) {
          return prev;
        }
        return newFilters;
      });
    }, 100);
    return () => clearTimeout(timeoutRef);
  }, [search]);

  return (
    <>
      <PageHeader title={i18n('carrierPortal.orders.orders')} />

      <ListPage
        heading={
          <PageHeading noMarginBottom leftSide={<Breadcrumb currentItem={i18n('carrierPortal.orders.orders')} />} />
        }
        filters={
          <SearchWithType
            value={search.value}
            onChange={(val) => setSearch((prev) => ({ ...prev, value: val }))}
            types={searchTypes}
            selectedType={search.type}
            onTypeChange={(val) => setSearch((prev) => ({ ...prev, type: val }))}
          />
        }
        pagination={
          <Pagination
            hasPrevious={page.hasPrevious}
            previous={page.previous}
            hasNext={page.hasNext}
            next={page.next}
            isFetching={page.isFetching}
          />
        }
      >
        <Table
          className="h-full"
          idKey="id"
          headers={tableHeaders}
          data={page.data}
          error={page.error}
          onRetry={page.retry}
          isLoading={page.isFetching}
          mapData={(order) => {
            const loadStops = order.lines.map((v) => v.stops.filter((s) => s.type === 'Load')).flat();
            const unloadStops = order.lines.map((v) => v.stops.filter((s) => s.type === 'Unload')).flat();
            const loadDates = Array.from(new Set(loadStops.map((s) => formatDate(s.date)))).join(', ');
            const unloadDates = Array.from(new Set(unloadStops.map((s) => formatDate(s.date)))).join(', ');
            const lineSales = order.lines.map((l) => l.purchases).flat();
            const saleTotal = calculateLinesTotalExclVat(lineSales);
            const requiredDocuments = order.requiredDocuments as RequiredDocument[];

            return [
              <Link to={order.id} className="link-text whitespace-nowrap">
                {order.orderNumber}
              </Link>,
              [
                ...new Set(
                  order.lines
                    .map((l) =>
                      l.stops
                        .filter((s) => s.type === 'Load')
                        .map((s) => s.location.city)
                        .flat(),
                    )
                    .flat(),
                ),
              ].join(', '),
              [
                ...new Set(
                  order.lines
                    .map((l) =>
                      l.stops
                        .filter((s) => s.type === 'Unload')
                        .map((s) => s.location.city)
                        .flat(),
                    )
                    .flat(),
                ),
              ].join(', '),
              loadDates,
              unloadDates,
              <div className="whitespace-nowrap">{`€ ${decimateAndFormatNumber(saleTotal, 2, {
                decimalSeperator: ',',
              })}`}</div>,
              <CarrierPortalRequiredDocsCell
                carrierId={carrierId}
                orderId={order.id}
                requiredDocuments={requiredDocuments}
                disableUpload={order.status === 'Cancelled'}
                onUploaded={async () => {
                  setRefreshKey((k) => k + 1);
                }}
              />,
            ];
          }}
        />
      </ListPage>
    </>
  );
};
